import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, FilePdf, FileText, EnvelopeOpen, EnvelopeSimple, CircleNotch, ClockCounterClockwise, Trash, CaretLeft, Bank } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { copyToClipboard } from "@/lib/clipboard";
import { exportAmendmentPDF, exportAmendmentTXT } from "@/lib/export";
import { getBankInfo, setBankInfo } from "@/lib/history";

// Build the addressee block from bank details.
function bankBlock(bank) {
  const lines = [];
  if (bank?.name?.trim()) lines.push(bank.name.trim());
  if (bank?.address?.trim()) lines.push(...bank.address.trim().split("\n"));
  return lines.join("\n");
}

export function AmendmentModal({ open, onClose, loading, amendment, browse = false, saved = [], onDeleteSaved }) {
  const { t, lang } = useI18n();
  const [pdfBusy, setPdfBusy] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [active, setActive] = useState(null); // currently displayed letter (current draft or a saved one)
  const [bank, setBank] = useState({ name: "", address: "", email: "" });

  useEffect(() => { setActive(amendment); }, [amendment]);
  useEffect(() => {
    if (!open) { setShowSaved(false); setActive(null); }
    else { setBank(getBankInfo()); if (browse) setShowSaved(true); }
  }, [open, browse]);

  const view = active || amendment;

  const updateBank = (patch) => {
    const next = { ...bank, ...patch };
    setBank(next);
    setBankInfo(next);
  };

  // Letter with the issuing-bank addressee prepended (used for every export + email).
  const composedBody = (letter) => {
    const block = bankBlock(bank);
    return block ? `${block}\n\n${letter || ""}` : (letter || "");
  };
  const composedView = () => ({ ...view, letter_body: composedBody(view?.letter_body) });

  const copy = async () => {
    if (!view) return;
    const ok = await copyToClipboard(composedBody(view.letter_body));
    if (ok) toast.success(t("md.amendCopied"));
    else toast.error(t("md.copyFailed"));
  };

  const downloadTxt = () => {
    if (!view) return;
    try {
      exportAmendmentTXT(composedView());
      toast.success(t("md.amendTxtDone"));
    } catch (e) {
      console.error("amendment txt export failed", e);
      toast.error(t("md.amendExportFailed"));
    }
  };

  const downloadPdf = async () => {
    if (!view || pdfBusy) return;
    setPdfBusy(true);
    try {
      await exportAmendmentPDF(composedView(), {
        lang: view.lang || lang,
        labels: {
          reqChanges: t("md.amendReqChanges"),
          from: t("md.amendFrom").replace(/[:：]\s*$/, ""),
          to: t("md.amendTo").replace(/[:：]\s*$/, ""),
          ucpRef: t("md.amendUcpRef"),
          disclaimer: t("md.amendDisclaimer"),
        },
      });
      toast.success(t("md.amendPdfDone"));
    } catch (e) {
      console.error("amendment pdf export failed", e);
      toast.error(t("md.amendExportFailed"));
    } finally {
      setPdfBusy(false);
    }
  };

  const openEmail = () => {
    if (!view) return;
    const to = encodeURIComponent((bank.email || "").trim());
    const subject = encodeURIComponent(view.subject || "");
    const body = encodeURIComponent(composedBody(view.letter_body));
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
    toast.success(t("md.amendEmailOpened"));
  };

  const openSaved = (it) => { setActive(it); setShowSaved(false); };
  const del = (e, id) => {
    e.stopPropagation();
    onDeleteSaved?.(id);
    if (active && active.id === id) setActive(amendment);
  };

  const listMode = showSaved || (!view && !loading);

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="amendment-modal">
          <div className="absolute inset-0 bg-lc-navy/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 24 }}
            className="relative flex max-h-[85vh] w-full max-w-2xl flex-col rounded-sm border border-lc-ink/10 bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-lc-ink/10 px-6 py-4">
              <h2 className="flex items-center gap-2 font-serif text-xl text-lc-ink">
                <EnvelopeOpen size={20} weight="duotone" className="text-lc-ocean" /> {t("md.amendTitle")}
              </h2>
              <div className="flex items-center gap-2">
                {!loading && (
                  <button onClick={() => setShowSaved((s) => !s)} data-testid="amendment-saved-toggle"
                    className={`inline-flex items-center gap-1.5 rounded-sm border px-2.5 py-1.5 text-[11px] font-semibold transition-colors duration-200 ${listMode ? "border-lc-ocean bg-lc-ocean/10 text-lc-ocean" : "border-lc-ink/15 text-lc-steel hover:text-lc-ink hover:bg-lc-paper"}`}>
                    <ClockCounterClockwise size={14} /> {t("md.amendSaved")}
                    {saved.length > 0 && (
                      <span className="ml-0.5 rounded-full bg-lc-ocean px-1.5 text-[10px] font-bold text-white" data-testid="amendment-saved-count">{saved.length}</span>
                    )}
                  </button>
                )}
                <button onClick={onClose} data-testid="amendment-close" className="text-lc-steel hover:text-lc-ink transition-colors duration-200">
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="lc-scroll flex-1 overflow-y-auto p-6">
              {listMode ? (
                <div data-testid="amendment-saved-list">
                  {saved.length === 0 ? (
                    <div className="flex flex-col items-center py-16 text-center" data-testid="amendment-saved-empty">
                      <ClockCounterClockwise size={40} weight="duotone" className="text-lc-ink/20" />
                      <p className="mt-4 text-sm text-lc-steel">{t("md.amendSavedEmpty")}</p>
                    </div>
                  ) : (
                    <div className="space-y-2.5">
                      {saved.map((it) => (
                        <div key={it.id} data-testid={`amendment-saved-item-${it.id}`}
                          onClick={() => openSaved(it)}
                          className="group cursor-pointer rounded-sm border border-lc-ink/10 p-3.5 transition-colors duration-200 hover:border-lc-ocean/40 hover:bg-lc-paper">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate text-xs font-semibold text-lc-ink">{it.subject || t("md.amendTitle")}</p>
                              <span className="mt-1 block font-mono text-[10px] text-lc-steel">{new Date(it.created_at).toLocaleString()}</span>
                            </div>
                            <button onClick={(e) => del(e, it.id)} data-testid={`amendment-saved-delete-${it.id}`}
                              className="shrink-0 rounded-sm border border-lc-ink/15 p-1.5 text-lc-steel hover:text-lc-critical transition-colors duration-200" title={t("md.delete")}>
                              <Trash size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : loading ? (
                <div className="flex flex-col items-center py-16" data-testid="amendment-loading">
                  <CircleNotch size={36} className="animate-spin text-lc-ocean" />
                  <p className="mt-4 font-mono text-xs uppercase tracking-widest text-lc-steel">{t("md.amendDrafting")}</p>
                </div>
              ) : view ? (
                <div data-testid="amendment-result">
                  {active && amendment && active.id !== amendment.id && (
                    <button onClick={() => setActive(amendment)} data-testid="amendment-back-current"
                      className="mb-3 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-lc-ocean hover:underline">
                      <CaretLeft size={12} /> {t("md.amendBackCurrent")}
                    </button>
                  )}

                  {/* Issuing-bank addressee (flows into every export + email) */}
                  <div className="mb-4 rounded-sm border border-lc-ink/10 bg-lc-paper/60 p-3.5" data-testid="amendment-bank-block">
                    <h4 className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-lc-steel">
                      <Bank size={13} weight="duotone" className="text-lc-ocean" /> {t("md.bankSection")}
                    </h4>
                    <div className="mt-2.5 grid gap-2.5 sm:grid-cols-2">
                      <input value={bank.name} onChange={(e) => updateBank({ name: e.target.value })}
                        placeholder={t("md.bankNamePh")} data-testid="amendment-bank-name"
                        className="w-full rounded-sm border border-lc-ink/15 bg-white px-2.5 py-2 text-xs text-lc-ink outline-none focus:border-lc-ocean transition-colors duration-200" />
                      <input value={bank.email} onChange={(e) => updateBank({ email: e.target.value })}
                        placeholder={t("md.bankEmailPh")} type="email" data-testid="amendment-bank-email"
                        className="w-full rounded-sm border border-lc-ink/15 bg-white px-2.5 py-2 text-xs text-lc-ink outline-none focus:border-lc-ocean transition-colors duration-200" />
                    </div>
                    <textarea value={bank.address} onChange={(e) => updateBank({ address: e.target.value })}
                      placeholder={t("md.bankAddressPh")} rows={2} data-testid="amendment-bank-address"
                      className="mt-2.5 w-full resize-none rounded-sm border border-lc-ink/15 bg-white px-2.5 py-2 text-xs text-lc-ink outline-none focus:border-lc-ocean transition-colors duration-200" />
                    <p className="mt-2 text-[10px] text-lc-steel">{t("md.bankHint")}</p>
                  </div>

                  <p className="font-semibold text-sm text-lc-ink">{view.subject}</p>
                  <pre className="mt-3 whitespace-pre-wrap rounded-sm border border-lc-ink/10 bg-lc-paper p-4 font-mono text-[12px] leading-relaxed text-lc-ink">{composedBody(view.letter_body)}</pre>
                  {(view.requested_amendments || []).length > 0 && (
                    <div className="mt-5">
                      <h4 className="font-mono text-[10px] uppercase tracking-widest text-lc-steel">{t("md.amendReqChanges")}</h4>
                      <div className="mt-2 space-y-2">
                        {view.requested_amendments.map((a, i) => (
                          <div key={i} className="rounded-sm border border-lc-ink/10 p-3 text-xs">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-lc-ink">{a.field}</span>
                              <span className="font-mono text-[10px] text-lc-ocean">{a.ucp_reference}</span>
                            </div>
                            <div className="mt-1 text-lc-steel"><span className="text-lc-critical">{t("md.amendFrom")}</span> {a.current}</div>
                            <div className="text-lc-steel"><span className="text-lc-ok">{t("md.amendTo")}</span> {a.requested}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {view && !loading && !listMode && (
              <div className="flex flex-wrap justify-end gap-2 border-t border-lc-ink/10 px-6 py-3.5">
                <button onClick={copy} data-testid="amendment-copy"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-lc-ink/15 px-3 py-2 text-xs font-semibold text-lc-ink hover:bg-lc-paper transition-colors duration-200">
                  <Copy size={14} /> {t("md.copyFull")}
                </button>
                <button onClick={downloadTxt} data-testid="amendment-download-txt"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-lc-ink/15 px-3 py-2 text-xs font-semibold text-lc-ink hover:bg-lc-paper transition-colors duration-200">
                  <FileText size={14} /> {t("md.downloadTxt")}
                </button>
                <button onClick={downloadPdf} disabled={pdfBusy} data-testid="amendment-download-pdf"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-lc-ink/15 px-3 py-2 text-xs font-semibold text-lc-ink hover:bg-lc-paper transition-colors duration-200 disabled:opacity-60">
                  {pdfBusy ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} />} {t("md.downloadPdf")}
                </button>
                <button onClick={openEmail} data-testid="amendment-email"
                  className="inline-flex items-center gap-1.5 rounded-sm bg-lc-navy px-3 py-2 text-xs font-semibold text-white hover:bg-lc-ocean transition-colors duration-200">
                  <EnvelopeSimple size={14} /> {t("md.openEmail")}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
